import { AfterViewChecked, ChangeDetectorRef, Component, computed, DestroyRef, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { ProjectNavMenuComponent } from '../../components';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {ProjectLinkService, ProjectNoteService, ProjectService, ProjectVersionService, QuickscanDraftService, StepService, ToastService, UserService} from '../../services';
import {ICurrentStepState, IProgressBarStep} from '../../interfaces';
import {forkJoin, from, fromEvent, of} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {ProjectDetails} from '../../models';
import {ProjectLink} from '../../models/project-analysis';
import {getMissingIntakeFields, isAllowedProjectLink, isMailLink} from '../../utils';
import {
    ChangeProjectTypeModalComponent,
    CloseProjectModalComponent,
    DeleteProjectModalComponent,
    DownloadPDFModalComponent,
    NewProjectVersionModalComponent,
    ProjectOnHoldModalComponent,
    ProjectVersionsListModalComponent,
    ShareProjectModalComponent
} from '../../components';
import {catchError, filter, map, switchMap, take} from 'rxjs/operators';
import {
    NewProjectVersionModalData,
    NewProjectVersionModalResult
} from '../../components/new-project-version-modal/new-project-version-modal.component';
import {VwuiModalService} from '@bvgo-ict/vwui-angular';
import {PDFPageKeys, ProjectStatus, ProjectSteps} from '../../enums';
import {ProjectLibraryItemStatus} from '../../models/project/project-libary-item';
import {IProjectVersionData} from '../../models/project/project-data';
import {AccessibleStepsService} from '../../services/accessible-steps/accessible-steps.service';
import {
    ProjectAnalysisQuestionsComponent
} from "../../components/project-analysis-questions/project-analysis-questions.component";
import {
    SubmitProjectIntakeModalComponent
} from '../../components/submit-project-intake-modal/submit-project-intake-modal.component';

@Component({
    selector: 'app-page-project-steps',
    templateUrl: './page-project-analysis-steps.component.html',
    styleUrls: ['./page-project-analysis-steps.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PageProjectAnalysisStepsComponent implements OnInit, AfterViewChecked {
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private stepService = inject(StepService);
    private accessibleStepsService = inject(AccessibleStepsService);
    private changeDetector = inject(ChangeDetectorRef);
    private translateService = inject(TranslateService);
    private toastService = inject(ToastService);
    private projectService = inject(ProjectService);
    private modalService = inject(VwuiModalService);
    private userService = inject(UserService);
    private projectVersionService = inject(ProjectVersionService);
    private projectNoteService = inject(ProjectNoteService);
    private quickscanDraftService = inject(QuickscanDraftService);
    private destroyRef = inject(DestroyRef);

    private readonly allProjectLinks = toSignal(inject(ProjectLinkService).links$, {initialValue: [] as ProjectLink[]});
    public readonly projectLinks = computed(() => this.allProjectLinks().filter((link) => isAllowedProjectLink(link.link)));
    public readonly isMailLink = isMailLink;

    public projectDetailsInvalid = false;
    public stepPrefix: string;
    public projectId: number;
    public project: ProjectDetails;
    public stepId: ProjectSteps;
    public stepProgress: number;
    public currentStepState: ICurrentStepState;
    public progressBarSteps: IProgressBarStep[];
    public blinkShown = false;
    private canChangeStatus = false;
    public projectSteps = ProjectSteps;
    public quickscanStepVisible = true;
    private redirectingAfterOwnerChange = false;


    @ViewChild(ProjectAnalysisQuestionsComponent)
    public quickscanStep?: ProjectAnalysisQuestionsComponent;

    @ViewChild(ProjectNavMenuComponent)
    public navMenu?: ProjectNavMenuComponent;

    ngOnInit() {
        this.stepService.onCurrentStepChanged()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.updateStep();
            });

        this.setData();
        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.setData();
                }
            });

        fromEvent(window, 'pagehide')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.saveOnPageHide());
    }

    /**
     * `pagehide` fires on tab/browser close (and more reliably on mobile than `beforeunload`,
     * without disabling the back/forward cache). Router's canDeactivate guard doesn't run here
     * since the SPA isn't navigating away, so this is the only chance to flush pending changes.
     */
    private saveOnPageHide(): void {
        if (!this.project || this.disabledSaveReason()) {
            return;
        }

        this.projectService.saveProjectDetailsOnUnload(this.project);
    }

    public openProjectLink(link: ProjectLink): void {
        if (!isAllowedProjectLink(link.link)) {
            return;
        }

        const url = link.link.trim();
        if (isMailLink(url)) {
            window.location.href = url;
            return;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    }

    ngAfterViewChecked() {
        // Prevents ExpressionChangedAfterItHasBeenCheckedError
        this.changeDetector.detectChanges();
    }

    /**
     * `redirectOnOwnerChange` is false when the navigation stays on this same project (just
     * switching steps), so changing the owner doesn't eject the user to the project list for
     * that; it's true when the guard detects the navigation is actually leaving the project.
     */
    public flushProjectSave(redirectOnOwnerChange = true): Promise<boolean> {
        // The owner-change redirect re-enters the canDeactivate guard; saving again would loop (PRM-171).
        if (!this.project || this.disabledSaveReason() || this.redirectingAfterOwnerChange) {
            return Promise.resolve(true);
        }

        const changingOwner = this.project.owner.emailAddress !== this.project.newOwner.emailAddress;

        return this.projectService.saveProjectDetails(this.project)
            .then(() => {
                this.quickscanDraftService.clear(this.project.id);
                if (changingOwner && redirectOnOwnerChange) {
                    this.redirectingAfterOwnerChange = true;
                    this.toastService.openSuccess(this.translateService.instant('PAGE_PROJECT_DETAILS.message_moved'));
                    this.router.navigate(['projecten'])
                        .then((navigated) => this.redirectingAfterOwnerChange = navigated)
                        .catch(() => this.redirectingAfterOwnerChange = false);
                    return false;
                }
                return true;
            })
            .catch((error) => {
                this.toastService.openError(error.code, this.translateService.instant('PAGE_PROJECT_DETAILS.error_message'));
                return true;
            });
    }

    public setProjectToArchived(event: Event): void {
        if (this.disabledForArchiving()) {
            event.stopImmediatePropagation();
            return;
        }

        const modal = this.modalService.open(
            DeleteProjectModalComponent,
            {
                data: {
                    projectDetails: this.project
                }
            }
        );

        modal.afterClosed.subscribe((isSubmitted) => {
            if (isSubmitted === true) {
                this.router.navigate(['projecten']);
            }
        });
    }

    public setProjectToClosed(event: Event): void {
        if (!this.enabledForClosing()) {
            event.stopImmediatePropagation();
            return;
        }

        const modal = this.modalService.open(
            CloseProjectModalComponent,
            {
                data: {
                    projectDetails: this.project
                }
            }
        );

        modal.afterClosed.subscribe((isSubmitted) => {
            if (isSubmitted === true) {
                this.router.navigate(['projecten']);
            }
        });
    }

    public changeProjectOnHoldStatus(event: Event): void {
        if (this.disabledForOnHold()) {
            event.stopImmediatePropagation();
            return;
        }

        const modal = this.modalService.open(
            ProjectOnHoldModalComponent,
            {
                data: {
                    projectDetails: this.project
                }
            }
        );

        modal.afterClosed.subscribe((isSubmitted: boolean) => {
            if (isSubmitted === true) {
                this.router.navigate(['projecten']);
            }
        });
    }

    public changeProjectType(event: Event): void {
        if (this.disabledForChangeProjectType()) {
            event.stopImmediatePropagation();
            return;
        }

        const modal = this.modalService.open(
            ChangeProjectTypeModalComponent,
            {
                data: {
                    projectId: this.project.id,
                    projectTypeId: this.project.projectType?.id
                }
            }
        );

        modal.afterClosed.subscribe((updatedProject: ProjectDetails | boolean) => {
            if (updatedProject instanceof ProjectDetails) {
                this.project = updatedProject;
                this.reloadQuickscanStep();
            }
        });
    }

    /**
     * The question set depends on the project type, so the quickscan step is
     * remounted to force it to refetch its definition instead of navigating
     * the user away to the project overview.
     */
    private reloadQuickscanStep(): void {
        this.quickscanStepVisible = false;
        this.changeDetector.detectChanges();
        this.quickscanStepVisible = true;
    }

    public confirmSubmitProjectIntake(): void {
        if (!this.quickscanStep || this.quickscanStep.definitiefDisabledReason()) {
            return;
        }

        const missingFields = getMissingIntakeFields(this.project?.projectInfo);
        if (missingFields.length > 0) {
            this.showMissingIntakeFields(missingFields);
            return;
        }

        this.modalService.open(SubmitProjectIntakeModalComponent, {data: {projectName: this.project.name}})
            .afterClosed
            .pipe(filter((confirmed) => confirmed === true))
            .subscribe(() => this.quickscanStep?.makeDefinitive());
    }

    private showMissingIntakeFields(missingFields: string[]): void {
        const fields = missingFields.map((field) => this.translateService.instant(field)).join(', ');

        this.toastService.open({
            type: 'error',
            icon: 'exclamation',
            duration: 8000,
            dismiss: true,
            message: this.translateService.instant('SUBMIT_PROJECT_INTAKE.missing_fields', {fields})
        });
    }

    public shareProject(event: Event): void {
        if (this.disabledForNonEditors()) {
            event.stopImmediatePropagation();
            return;
        }

        const modal = this.modalService.open(
            ShareProjectModalComponent,
            {
                closeOnBackdropClick: false,
                modalClass: 'medium-width-modal',
                data: {
                    projectDetails: this.project
                }
            }
        );

        modal.afterClosed.subscribe((newDetails: ProjectDetails | boolean) => {
            if (newDetails !== false && newDetails instanceof ProjectDetails) {
                this.project = newDetails;
            }
        });
    }

    public projectDetailsValid($event: boolean) {
        this.projectDetailsInvalid = ($event === false);
    }

    public projectDetailsChanged($event: ProjectDetails) {
        this.project = $event;
        this.project.changed();
    }

    public downloadResult(): void {
        this.modalService.open(
            DownloadPDFModalComponent, {
                closeOnBackdropClick: false,
                closeOnEsc: false,
                modalClass: 'medium-modal',
                data: {
                    keys: {
                        [PDFPageKeys.PROJECT_DETAILS]: !this.isInExecution(),
                        [PDFPageKeys.QUICKSCAN]: this.stepId >= ProjectSteps.QUICKSCAN && !this.isInExecution(),
                        [PDFPageKeys.RISKS_OPPORTUNITIES]: this.stepId >= ProjectSteps.RISKS,
                        [PDFPageKeys.RISK_RAINBOW]: this.stepId >= ProjectSteps.RAINBOW || this.isInExecution(),
                        [PDFPageKeys.TIS_QUICKSCAN]: false,
                        [PDFPageKeys.TIS]: this.stepId >= ProjectSteps.TIS_QUESTIONS,
                        [PDFPageKeys.MANDATING_FLOW]: this.stepId >= ProjectSteps.MANDATING && this.canMandate()
                    },
                    projectDetails: this.project
                }
            }
        );
    }

    public openNewVersionModal(): void {
        if (!this.project || this.project.isEditLocked()) {
            return;
        }

        this.projectVersionService.list(this.project.id)
            .pipe(
                take(1),
                map((versions) => versions[0] ?? null)
            )
            .subscribe((latestVersion) => {
                this.project.projectVersion = latestVersion;
                this.openNewVersionModalFor(latestVersion);
            });
    }

    private openNewVersionModalFor(latest: IProjectVersionData | null): void {
        const data: NewProjectVersionModalData = {
            project: this.project,
            currentVersion: latest ? `${latest.majorVersion}.${latest.minorVersion}` : null
        };
        const modal = this.modalService.open(NewProjectVersionModalComponent, {
            closeOnBackdropClick: false,
            data
        });

        modal.afterClosed
            .pipe(
                take(1),
                filter(Boolean),
                switchMap((result: NewProjectVersionModalResult) =>
                    this.projectVersionService.create(this.project.id, result.bumpKind, result.note)
                ),
                switchMap((version) => {
                    const versionLabel = `${version.majorVersion}.${version.minorVersion}`;
                    const note = version.note?.trim();
                    const message = note
                        ? this.translateService.instant('NEW_PROJECT_VERSION.note.auto_message_with_note', {version: versionLabel, note})
                        : this.translateService.instant('NEW_PROJECT_VERSION.note.auto_message', {version: versionLabel});
                    return this.projectNoteService.create(this.project.id, message).pipe(
                        catchError((error) => {
                            console.warn('auto-note creation failed', error);
                            return of(void 0);
                        }),
                        map(() => version)
                    );
                })
            )
            .subscribe((version) => {
                // The create call returns the new version; no need to refetch the project
                this.project.projectVersion = version;

                this.toastService.openSuccess(this.translateService.instant('NEW_PROJECT_VERSION.toast.created', {
                    version: `${version.majorVersion}.${version.minorVersion}`
                }));
            });
    }

    public openVersionsListModal(): void {
        if (!this.project) {
            return;
        }

        this.modalService.open(ProjectVersionsListModalComponent, {
            closeOnBackdropClick: true,
            modalClass: 'large-modal',
            data: {projectId: this.project.id}
        });
    }

    public disabledForArchiving(): string {
        if (!this.projectService.isOwner(this.project)) {
            return 'DISABLED.not_owner';
        }
        if (!this.userService.isTenderManager()) {
            return 'DISABLED.not_tender_manager';
        }
        if (this.project.status === ProjectStatus.MANDATING) {
            return 'DISABLED.in_mandate';
        }
        return '';
    }

    public disabledForChangeProjectType(): string {
        if (!this.projectService.isOwner(this.project)) {
            return 'DISABLED.not_owner';
        }
        if (this.project.status !== ProjectStatus.INITIAL) {
            return 'DISABLED.project_type_quickscan_finalized';
        }
        return '';
    }

    public disabledForOnHold(): string {
        if (this.project?.status === ProjectStatus.MANDATING) {
            return 'DISABLED.in_mandate';
        }

        // RISC-802 - Only tender managers or project owner are allowed to change the project status to on hold
        if (!this.projectService.isOwner(this.project) && !this.projectService.isCompanyTenderManager(this.project)) {
            return 'DISABLED.no_on_hold_rights';
        }

        return '';
    }

    public disabledSaveReason(): string {
        if (this.projectService.canEditProject(this.project) ||
            // RISC-668 - Risk managers and (company) admins are allowed to change the project owner even if they do not have write access
            this.projectService.canManageCompany(this.project)) {
            if (this.projectDetailsInvalid) {
                return 'DISABLED.form_invalid';
            }
            if (this.project.isClosed()) {
                return 'DISABLED.project_closed';
            }
            if (this.project.isEditLocked()) {
                return 'DISABLED.project_locked_for_edit';
            }
        } else {
            return 'DISABLED.no_edit_rights';
        }
        return '';
    }

    public disabledForNonEditors(): string {
        if (this.projectService.canEditProjectDetails(this.project)) {
            if (this.projectDetailsInvalid) {
                return 'DISABLED.form_invalid';
            }
        } else {
            return 'DISABLED.no_edit_rights';
        }
        return '';
    }

    public showInExecutionSteps(): boolean {
        return this.project && (this.project.status === ProjectStatus.IN_EXECUTION || this.project.isClosed());
    }

    private canMandate(): boolean {
        return this.project && this.project.isInMandateProcess() || this.project.isMandated();
    }

    private setData(): void {
        this.stepPrefix = `/projecten/${this.route.snapshot.paramMap.get('projectId')}`;
        this.projectId = parseInt(this.route.snapshot.paramMap.get('projectId'), 10);
        const newStepId = parseInt(this.route.snapshot.paramMap.get('stepId'), 10) as ProjectSteps;

        // PRM 2.0 no longer has a separate TIS quickscan page
        if (newStepId === ProjectSteps.TIS_QUICKSCAN) {
            this.router.navigate([this.stepPrefix, 'step', ProjectSteps.QUICKSCAN.toString()]);
            return;
        }

        this.stepId = newStepId;
        this.stepService.setCurrentStep(this.stepId);

        // Only retrieve the project when we don't have one to prevent clearing filled in forms
        if (!this.project) {
            forkJoin({
                project: from(this.projectService.getProjectDetails(this.projectId)),
                versions: this.projectVersionService.list(this.projectId)
            }).pipe(
                // The frontend maps the versions list onto project.projectVersion itself,
                // since the /details response can lag behind right after a new version was created.
                map(({project, versions}) => Object.assign(project, {projectVersion: versions[0] ?? null})),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe((project) => {
                this.project = project;
                this.canChangeStatus = this.projectService.isOwner(this.project) || this.projectService.isProjectRiskManager(this.project);
                this.blinkShown = !this.canChangeStatus;
                this.updateStep();
            });
        }
    }

    private updateStep(): void {
        this.stepProgress = this.getStepProgress();

        this.currentStepState = this.stepService.getCurrentStepState();

        const progressBarSteps: IProgressBarStep[] = [];
        const accessibleSteps: ProjectSteps[] = this.accessibleStepsService.accessibleSteps
            .filter((step) => step !== ProjectSteps.TIS_QUICKSCAN);

        const amountRiskProposals = this.project ? this.project.projectLibraryItems?.filter((item) => {
            return item.status === ProjectLibraryItemStatus.PROPOSED && item.libraryType === 'risks';
        }).length : 0;
        const amountOpportunityProposals = this.project ? this.project.projectLibraryItems?.filter((item) => {
            return item.status === ProjectLibraryItemStatus.PROPOSED && item.libraryType === 'opportunities';
        }).length : 0;
        accessibleSteps.forEach((accessibleStep, index) => {
            let tooltipText = this.getStepLabel(accessibleStep);

            let stepHasBlink = false;
            let amountProposals = 0;
            if (index === 2 && amountRiskProposals > 0) {
                stepHasBlink = true;
                amountProposals = amountRiskProposals;
            } else if (index === 3 && amountOpportunityProposals > 0) {
                stepHasBlink = true;
                amountProposals = amountOpportunityProposals;
            }

            if (amountProposals > 0) {
                if (amountProposals === 1) {
                    tooltipText += ': ' + amountProposals + this.translateService.instant(`STEPS_BLINK.single_proposal`);
                } else {
                    tooltipText += ': ' + amountProposals + this.translateService.instant(`STEPS_BLINK.multiple_proposals`);
                }
            }

            const currentStepIndex = accessibleSteps.indexOf(this.currentStepState.currentStep);
            progressBarSteps.push({
                urlSlugs: [this.stepPrefix, 'step', accessibleStep.toString()],
                progress: Math.round((100 / (accessibleSteps.length - 1)) * index),
                active: index <= currentStepIndex,
                blink: !this.blinkShown && this.canChangeStatus && stepHasBlink
                ,
                tooltip: tooltipText
            });
        });

        this.progressBarSteps = progressBarSteps;

        // We only want the blinking animation to be shown once when the project is opened, otherwise it would blink
        // On every page switch which would be very annoying to the user
        this.blinkShown = true;
    }

    private getStepLabel(step: ProjectSteps): string {
        // PRM 2.0 flow overrides some step labels (e.g. step 8 "Projectanalyse")
        const v2Key = `STEPS_V2.${step}`;
        const v2Label = this.translateService.instant(v2Key);
        if (v2Label !== v2Key) {
            return v2Label;
        }

        return this.translateService.instant(`STEPS.${step}`, {tisLabelShort: this.project ? this.project.projectInfo.tisLabelShort : ''});
    }

    private getStepProgress() {
        const perStepPercentage = 100 / (this.stepService.getStepCount() - 1);

        return perStepPercentage * (this.stepService.getCurrentStepIndex());
    }

    enabledForClosing() {
        return this.isInExecution() && this.projectService.isOwner(this.project);
    }

    private isInExecution(): boolean {
        return this.project && this.project.status === ProjectStatus.IN_EXECUTION;
    }

    isClosed() {
        return this.project && this.project.status === ProjectStatus.CLOSED;
    }
}
